/*
  ZEVQILO Atelier — product.js
  Product page: variant changes (Section Rendering API), gallery counter/thumbnails/fullscreen viewer,
  mobile sticky add-to-bag, recently viewed tracking, and lazy-loaded recommendation sections.
*/
(() => {
  const theme = window.theme;
  const RECENT_KEY = 'zq:recent:v1';
  /* Input that scrolls a photo track by hand (touch, trackpad or mouse wheel, scrollbar drag). */
  const USER_SCROLL_EVENTS = ['pointerdown', 'touchstart', 'wheel'];

  /* Product info: variant changes ---------------------------------------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.trackRecentlyViewed();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.controller?.abort();
    }

    onChange(event) {
      if (!event.target.closest('variant-picker')) return;
      const picker = event.target.closest('variant-picker');
      const legend = event.target.closest('fieldset')?.querySelector('[data-selected-value]');
      if (legend) legend.textContent = event.target.value;
      const ids = Array.from(picker.querySelectorAll('input:checked')).map((input) => input.dataset.optionValueId);
      this.fetchVariant(ids);
    }

    async fetchVariant(optionValueIds) {
      this.controller?.abort();
      this.controller = new AbortController();
      this.setAttribute('aria-busy', 'true');
      const url = `${this.productUrl}?option_values=${optionValueIds.join(',')}&section_id=${this.sectionId}`;
      try {
        const doc = await theme.fetchHTML(url, { signal: this.controller.signal });
        const next = doc.querySelector(`product-info[data-section-id="${this.sectionId}"]`);
        if (!next) return;
        this.update(next);
      } catch (error) {
        if (error.name !== 'AbortError') theme.announce(theme.strings.networkError);
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    update(next) {
      const selectors = [
        '[data-product-price]',
        '[data-product-sku]',
        '[data-buy-buttons]',
        '[data-sticky-price]',
        '[data-sticky-button]',
      ];
      selectors.forEach((selector) => {
        const incoming = next.querySelector(selector);
        const current = this.querySelector(selector);
        if (incoming && current) current.replaceWith(incoming);
      });

      /* Refresh availability markers in the picker without losing focus. */
      const picker = this.querySelector('variant-picker');
      const incomingPicker = next.querySelector('variant-picker');
      if (picker && incomingPicker) {
        incomingPicker.querySelectorAll('.variant-picker__value').forEach((value, index) => {
          const currentValue = picker.querySelectorAll('.variant-picker__value')[index];
          if (currentValue) currentValue.className = value.className;
        });
      }

      const variantJson = next.querySelector('[data-selected-variant]');
      if (!variantJson) return;
      const current = this.querySelector('[data-selected-variant]');
      if (current) current.textContent = variantJson.textContent;

      let variant;
      try {
        variant = JSON.parse(variantJson.textContent);
      } catch (error) {
        return;
      }
      if (!variant) return;

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.featured_media?.id) {
        this.querySelector('media-gallery')?.showMedia(String(variant.featured_media.id));
      }

      const statusText = variant.available ? '' : theme.strings.soldOut;
      theme.announce([variant.title, statusText].filter(Boolean).join(' — '));
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }

    trackRecentlyViewed() {
      const handle = this.dataset.productHandle;
      if (!handle) return;
      try {
        const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]').filter((h) => h !== handle);
        list.unshift(handle);
        window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 12)));
      } catch (error) {
        /* Storage unavailable: recently viewed simply stays empty. */
      }
    }
  }
  customElements.define('product-info', ProductInfo);

  /* Gallery --------------------------------------------------------------
     Phones: a swipeable row of photos with a thin position bar. Desktop, 'thumbnails' layout: one photo
     at a time in a square frame, chosen from a strip of thumbnails beside it, with a cross-fade between
     photos. Desktop, 'grid' layout: every photo on the page in two columns. */
  const STAGE_QUERY = '(min-width: 900px)';
  const FADE_MS = 460;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const whenIdle = (callback) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 4000 });
    } else {
      window.setTimeout(callback, 1500);
    }
  };

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-media-list]');
      this.counter = this.querySelector('[data-media-current]');
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-index]'));
      this.lightbox = document.getElementById(`Lightbox-${this.dataset.sectionId}`);
      if (!this.list) return;

      /* The chosen photo, and the one the frame is showing (they differ while a photo loads). */
      this.index = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')));
      this.shown = this.index;
      this.swapToken = 0;

      this.onScroll = theme.debounce(() => this.syncFromScroll(), 60);
      this.onClick = this.onClick.bind(this);
      this.list.addEventListener('scroll', this.onScroll, { passive: true });
      this.addEventListener('click', this.onClick);

      if (this.dataset.layout === 'thumbnails') this.setUpStage();
    }

    disconnectedCallback() {
      this.list?.removeEventListener('scroll', this.onScroll);
      this.removeEventListener('click', this.onClick);
      this.stageQuery?.removeEventListener('change', this.onModeChange);
      this.railList?.removeEventListener('scroll', this.onRailScroll);
      this.railList?.removeEventListener('keydown', this.onRailKeydown);
      this.rail?.removeEventListener('pointerover', this.onWarm);
      this.rail?.removeEventListener('focusin', this.onWarm);
      this.railObserver?.disconnect();
      window.clearTimeout(this.leaveTimer);
    }

    get items() {
      return Array.from(this.list.children);
    }

    /* Desktop with the thumbnails layout: one photo at a time. */
    get isStage() {
      return Boolean(this.stageQuery?.matches);
    }

    isCarousel() {
      return this.list.scrollWidth > this.list.clientWidth + 4;
    }

    setUpStage() {
      this.stageQuery = window.matchMedia(STAGE_QUERY);
      this.rail = this.querySelector('[data-thumb-rail]');
      this.railList = this.querySelector('[data-thumb-list]');
      this.prevArrow = this.querySelector('[data-thumbs-prev]');
      this.nextArrow = this.querySelector('[data-thumbs-next]');

      this.onModeChange = () => this.syncMode();
      this.onRailScroll = () => {
        if (this.railFrame) return;
        this.railFrame = window.requestAnimationFrame(() => {
          this.railFrame = 0;
          this.updateRail();
        });
      };
      this.onRailKeydown = this.onRailKeydown.bind(this);
      /* Pointing at a thumbnail starts loading its photo, so the switch is instant. */
      this.onWarm = (event) => {
        const thumb = event.target.closest?.('[data-thumb-index]');
        if (thumb) this.warm(Number(thumb.dataset.thumbIndex));
      };

      this.stageQuery.addEventListener('change', this.onModeChange);
      if (this.railList) {
        this.railList.addEventListener('scroll', this.onRailScroll, { passive: true });
        this.railList.addEventListener('keydown', this.onRailKeydown);
        this.rail.addEventListener('pointerover', this.onWarm);
        this.rail.addEventListener('focusin', this.onWarm);
        if ('ResizeObserver' in window) {
          this.railObserver = new ResizeObserver(() => this.updateRail());
          this.railObserver.observe(this.railList);
        }
      }
      this.syncMode();

      /* Photos fade in only when chosen, never with the page itself. */
      window.requestAnimationFrame(() => this.classList.add('is-ready'));

      /* Once the page has settled, fetch the other photos in the background. */
      const warmAll = () => {
        if (this.isStage) this.items.forEach((item, index) => this.warm(index));
      };
      if (document.readyState === 'complete') {
        whenIdle(warmAll);
      } else {
        window.addEventListener('load', () => whenIdle(warmAll), { once: true });
      }
    }

    /* Moving between phone and desktop widths keeps the same photo chosen. */
    syncMode() {
      if (this.isStage) {
        /* Each photo is shown whole: the scroll-in curtain belongs to the swipeable row. */
        this.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
        this.swap(this.index, false);
        this.updateRail();
        this.revealThumb(this.index, false);
      } else {
        const item = this.items[this.index];
        if (item && this.isCarousel()) this.list.scrollTo({ left: item.offsetLeft, behavior: 'auto' });
      }
    }

    onClick(event) {
      if (event.target.closest('[data-thumbs-prev]')) {
        this.scrollRail(-1);
        return;
      }
      if (event.target.closest('[data-thumbs-next]')) {
        this.scrollRail(1);
        return;
      }
      const thumb = event.target.closest('[data-thumb-index]');
      if (thumb) {
        this.scrollToIndex(Number(thumb.dataset.thumbIndex));
        return;
      }
      const opener = event.target.closest('[data-open-lightbox]');
      if (opener) this.openLightbox(opener.dataset.openLightbox, opener);
    }

    /* Up and down arrows (and Home and End) move along the thumbnails and show each photo. */
    onRailKeydown(event) {
      const thumb = event.target.closest('[data-thumb-index]');
      if (!thumb) return;
      const railThumbs = Array.from(this.railList.querySelectorAll('[data-thumb-index]'));
      const at = railThumbs.indexOf(thumb);
      let next = null;
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = Math.min(railThumbs.length - 1, at + 1);
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = Math.max(0, at - 1);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = railThumbs.length - 1;
      if (next === null) return;
      event.preventDefault();
      railThumbs[next].focus();
      this.scrollToIndex(Number(railThumbs[next].dataset.thumbIndex));
    }

    scrollToIndex(index) {
      const item = this.items[index];
      if (!item) return;
      if (this.isStage) {
        this.select(index);
        return;
      }
      const reduce = reducedMotion();
      if (this.isCarousel()) {
        this.list.scrollTo({ left: item.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      } else {
        item.scrollIntoView({ block: 'center', behavior: reduce ? 'auto' : 'smooth' });
      }
      this.index = index;
      this.setCurrent(index);
    }

    showMedia(mediaId) {
      const index = this.items.findIndex((item) => item.dataset.mediaId === mediaId);
      if (index > -1) this.scrollToIndex(index);
    }

    /* Desktop: choose a photo. The thumbnail frame moves at once; the photo fades in once it can be
       drawn (or after a short wait on a slow connection). */
    select(index) {
      this.index = index;
      this.setCurrent(index);
      this.revealThumb(index);
      if (index === this.shown) {
        this.swapToken += 1;
        this.swap(index, false);
        return;
      }
      const token = ++this.swapToken;
      const img = this.items[index].querySelector('img');
      this.warm(index);
      const ready = img && !img.complete ? Promise.race([img.decode().catch(() => {}), wait(700)]) : Promise.resolve();
      ready.then(() => {
        if (token === this.swapToken) this.swap(index, true);
      });
    }

    swap(index, animate) {
      const items = this.items;
      const next = items[index];
      if (!next) return;
      const prev = index === this.shown ? null : items[this.shown];
      window.clearTimeout(this.leaveTimer);
      items.forEach((item) => {
        if (item !== next) item.classList.remove('is-active');
        if (item !== prev) item.classList.remove('is-leaving');
      });
      if (prev) {
        /* A photo shown again later should not replay its entrance (motion.css). */
        prev.classList.add('is-settled');
        this.pauseMedia(prev);
        if (animate && !reducedMotion()) {
          prev.classList.add('is-leaving');
          this.leaveTimer = window.setTimeout(() => prev.classList.remove('is-leaving'), FADE_MS);
        } else {
          prev.classList.remove('is-leaving');
        }
      }
      next.classList.add('is-active');
      this.shown = index;
    }

    /* A video left playing out of sight would carry on: pause it (an external player only listens if
       it was loaded with its API enabled). */
    pauseMedia(item) {
      item.querySelectorAll('video').forEach((video) => video.pause());
      item.querySelectorAll('iframe').forEach((frame) => {
        const command = frame.src.includes('vimeo')
          ? { method: 'pause' }
          : { event: 'command', func: 'pauseVideo', args: '' };
        frame.contentWindow?.postMessage(JSON.stringify(command), '*');
      });
    }

    /* Start loading a photo that is not laid out yet. */
    warm(index) {
      const img = this.items[index]?.querySelector('img');
      if (img && img.loading === 'lazy') img.loading = 'eager';
    }

    /* Arrows and faded ends appear only where more thumbnails are out of sight. */
    updateRail() {
      if (!this.railList || !this.isStage) return;
      const list = this.railList;
      const max = list.scrollHeight - list.clientHeight;
      const canPrev = max > 1 && list.scrollTop > 1;
      const canNext = max > 1 && list.scrollTop < max - 1;
      this.rail.classList.toggle('can-scroll-prev', canPrev);
      this.rail.classList.toggle('can-scroll-next', canNext);
      if (this.prevArrow) this.prevArrow.hidden = !canPrev;
      if (this.nextArrow) this.nextArrow.hidden = !canNext;
    }

    /* The round arrows move the strip by two thumbnails. */
    scrollRail(direction) {
      const list = this.railList;
      if (!list) return;
      const item = list.querySelector('.product__thumbs-item');
      const gap = parseFloat(window.getComputedStyle(list).rowGap) || 0;
      const step = item ? (item.offsetHeight + gap) * 2 : list.clientHeight * 0.6;
      list.scrollBy({ top: direction * step, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }

    /* Keep the chosen thumbnail clear of the faded ends. */
    revealThumb(index, animate = true) {
      if (!this.railList || !this.isStage) return;
      const list = this.railList;
      const item = list.querySelector(`[data-thumb-index="${index}"]`)?.closest('li');
      if (!item) return;
      const fade = parseFloat(window.getComputedStyle(list).scrollPaddingTop) || 0;
      const top = item.offsetTop;
      const bottom = top + item.offsetHeight;
      let target = null;
      if (top - fade < list.scrollTop) {
        target = top - fade;
      } else if (bottom + fade > list.scrollTop + list.clientHeight) {
        target = bottom + fade - list.clientHeight;
      }
      if (target === null) return;
      list.scrollTo({ top: Math.max(0, target), behavior: animate && !reducedMotion() ? 'smooth' : 'auto' });
    }

    syncFromScroll() {
      if (this.isStage || !this.isCarousel()) return;
      const position = this.list.scrollLeft / (this.list.clientWidth || 1);
      const index = Math.round(position);
      /* Only once the row has come to rest on a photo, not while a smooth scroll is passing others. */
      if (Math.abs(position - index) > 0.02) return;
      this.index = index;
      this.setCurrent(index);
    }

    setCurrent(index) {
      if (this.counter) this.counter.textContent = String(index + 1);
      this.thumbs.forEach((thumb) => {
        if (Number(thumb.dataset.thumbIndex) === index) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    openLightbox(mediaId, trigger) {
      this.lightbox?.querySelector('product-lightbox')?.openAt(mediaId, trigger);
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* Full-screen viewer: swipe or arrow keys between photos; click to zoom on desktop.
     On touch screens the browser's own pinch-zoom is left alone. */
  class ProductLightbox extends HTMLElement {
    connectedCallback() {
      this.dialog = this.closest('dialog');
      this.track = this.querySelector('[data-lightbox-track]');
      this.slides = Array.from(this.track?.children || []);
      this.counter = this.querySelector('[data-lightbox-current]');
      this.prevButton = this.querySelector('[data-lightbox-prev]');
      this.nextButton = this.querySelector('[data-lightbox-next]');
      this.index = 0;
      this.pending = null;
      if (!this.dialog || !this.track) return;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.onScroll = theme.debounce(() => this.syncFromScroll(), 80);
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onMove = this.onMove.bind(this);
      this.onClose = () => this.resetZoom();
      /* A swipe, wheel or drag takes over from a scroll started by goTo. */
      this.onUserScroll = () => {
        this.pending = null;
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      USER_SCROLL_EVENTS.forEach((type) => this.track.addEventListener(type, this.onUserScroll, { passive: true }));
      this.addEventListener('click', this.onClick);
      this.addEventListener('mousemove', this.onMove);
      this.dialog.addEventListener('keydown', this.onKeydown);
      this.dialog.addEventListener('dialog:close', this.onClose);
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      USER_SCROLL_EVENTS.forEach((type) => this.track?.removeEventListener(type, this.onUserScroll));
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('mousemove', this.onMove);
      this.dialog?.removeEventListener('keydown', this.onKeydown);
      this.dialog?.removeEventListener('dialog:close', this.onClose);
    }

    openAt(mediaId, trigger) {
      const found = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      theme.openDialog(this.dialog, trigger);
      window.requestAnimationFrame(() => this.goTo(Math.max(0, found), false));
    }

    goTo(index, animate = true) {
      if (!this.slides.length) return;
      this.index = Math.max(0, Math.min(this.slides.length - 1, index));
      /* Where the scroll started below is heading (see syncFromScroll). */
      this.pending = this.index;
      this.resetZoom();
      this.loadAround(this.index);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: animate && !reduce ? 'smooth' : 'auto' });
      this.update();
    }

    loadAround(index) {
      [index - 1, index, index + 1].forEach((i) => {
        const img = this.slides[i]?.querySelector('img');
        if (img && img.loading !== 'eager') img.loading = 'eager';
      });
    }

    syncFromScroll() {
      const position = this.track.scrollLeft / (this.track.clientWidth || 1);
      const index = Math.round(position);
      /* Follow the scroll only once it has come to rest on a photo (a swipe always does, the track snaps). */
      if (Math.abs(position - index) > 0.02) return;
      /* A smooth scroll started by the buttons or arrow keys passes other photos on its way, and a slow
         frame can make it look settled on one of them: taking the index from there would undo the step.
         Until that scroll reaches its photo, the photo it is heading for stays current. */
      if (this.pending !== null) {
        if (index !== this.pending) return;
        this.pending = null;
      }
      if (index === this.index) return;
      this.index = index;
      this.resetZoom();
      this.loadAround(index);
      this.update();
    }

    update() {
      if (this.counter) this.counter.textContent = String(this.index + 1);
      if (this.prevButton) this.prevButton.disabled = this.index === 0;
      if (this.nextButton) this.nextButton.disabled = this.index === this.slides.length - 1;
    }

    onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.index + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.index - 1);
      } else if (event.key === 'Home' || event.key === 'End') {
        /* First and last photo. Left to the browser, these would scroll the track on their own and
           race the arrow keys. */
        event.preventDefault();
        this.goTo(event.key === 'Home' ? 0 : this.slides.length - 1);
      }
    }

    onClick(event) {
      if (event.target.closest('[data-lightbox-prev]')) {
        this.goTo(this.index - 1);
        return;
      }
      if (event.target.closest('[data-lightbox-next]')) {
        this.goTo(this.index + 1);
        return;
      }
      const frame = event.target.closest('[data-zoom-frame]');
      if (!frame || !this.finePointer.matches) return;
      const zoomIn = !frame.classList.contains('is-zoomed');
      this.resetZoom();
      if (zoomIn) {
        this.pan(frame, event);
        frame.classList.add('is-zoomed');
      }
    }

    onMove(event) {
      const frame = event.target.closest('[data-zoom-frame].is-zoomed');
      if (frame) this.pan(frame, event);
    }

    pan(frame, event) {
      const rect = frame.getBoundingClientRect();
      frame.style.setProperty('--zoom-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
      frame.style.setProperty('--zoom-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
    }

    resetZoom() {
      this.querySelectorAll('.is-zoomed').forEach((frame) => frame.classList.remove('is-zoomed'));
    }
  }
  customElements.define('product-lightbox', ProductLightbox);

  /* Mobile sticky add to bag --------------------------------------------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      this.info = this.closest('product-info');
      this.media = window.matchMedia('(max-width: 899px)');
      this.mainVisible = true;
      this.footerVisible = false;
      this.hidden = false;
      this.observe();
      this.onMedia = () => this.update();
      this.media.addEventListener('change', this.onMedia);
    }

    disconnectedCallback() {
      this.unobserve();
      this.media?.removeEventListener('change', this.onMedia);
    }

    unobserve() {
      this.mainObserver?.disconnect();
      this.footerObserver?.disconnect();
    }

    /* The bar shows once the page has scrolled past the main button, and not over the footer.
       Everything below the screen counts as "not yet passed": the observer's area reaches far down, so
       a jump straight from the bottom of the page back to the top (a tap on the status bar, the Home
       key) is noticed even though the button never came on screen on the way. */
    observe() {
      this.unobserve();
      this.mainObserver = new IntersectionObserver(
        ([entry]) => {
          this.mainVisible = entry.isIntersecting || entry.boundingClientRect.top > 0;
          this.update();
        },
        { rootMargin: '0px 0px 100000px 0px' }
      );
      this.footerObserver = new IntersectionObserver(([entry]) => {
        this.footerVisible = entry.isIntersecting;
        this.update();
      });
      const mainButton = this.info?.querySelector('[data-add-button]');
      if (mainButton) this.mainObserver.observe(mainButton);
      const footer = document.querySelector('.site-footer');
      if (footer) this.footerObserver.observe(footer);
      this.update();
    }

    update() {
      const show = this.media.matches && !this.mainVisible && !this.footerVisible;
      this.classList.toggle('is-visible', show);
      this.setAttribute('aria-hidden', String(!show));
      this.querySelectorAll('button').forEach((button) => {
        button.tabIndex = show ? 0 : -1;
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* Re-observe the main button after a variant swap replaces it. */
  document.addEventListener('variant:change', (event) => {
    const sticky = event.target.querySelector?.('sticky-atc');
    if (sticky) sticky.observe();
  });

  /* Recommendations (related / complementary) ----------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.observer.disconnect();
            this.load();
          }
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    /* The element itself stays in the layout (zero height while empty) so it can be observed;
       only the section inside it is hidden until products arrive. */
    async load() {
      const section = this.querySelector('[data-hide-when-empty]');
      try {
        const doc = await theme.fetchHTML(this.dataset.url);
        const incoming = doc.querySelector('[data-recommendations-target]');
        if (!incoming?.querySelector('[data-product-card]')) {
          section?.setAttribute('hidden', '');
          return;
        }
        this.querySelector('[data-recommendations-target]')?.replaceWith(incoming);
        section?.removeAttribute('hidden');
        this.querySelector('scroll-rail')?.init?.();
      } catch (error) {
        section?.setAttribute('hidden', '');
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* Recently viewed ------------------------------------------------------- */
  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const exclude = this.dataset.exclude;
      const limit = Number(this.dataset.limit) || 4;
      let handles = [];
      try {
        handles = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      } catch (error) {
        handles = [];
      }
      handles = handles.filter((h) => h !== exclude).slice(0, limit);
      if (!handles.length) return;

      const track = this.querySelector('[data-rail-track]');
      const cards = await Promise.all(
        handles.map(async (handle) => {
          try {
            const url = theme.sectionUrl(`${theme.routes.root}products/${encodeURIComponent(handle)}`, 'product-card-render');
            const response = await fetch(url);
            if (!response.ok) return null;
            const card = theme.parseHTML(await response.text()).querySelector('[data-product-card]');
            if (!card) return null;
            const li = document.createElement('li');
            li.append(card);
            return li;
          } catch (error) {
            return null;
          }
        })
      );
      const items = cards.filter(Boolean);
      if (!items.length || !track) return;
      track.replaceChildren(...items);
      this.closest('[data-hide-when-empty]')?.removeAttribute('hidden');
      this.querySelector('scroll-rail')?.init?.();
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);
})();
